import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { showAdminError, showAdminSuccess } from '../Common/adminResponseAlert';
import {
  FaBell,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaFilter,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUsers,
  FaExclamationTriangle,
  FaPlus,
  FaKey,
} from 'react-icons/fa';
import Navbar from '../../../../ReusableBlocks/Navbar';
import CreateAccountModal from './Components/CreateAccountModal';
import CreateAccountSuccessModal from './Components/CreateAccountSuccessModal';
import ResetPasswordModal from './Components/ResetPasswordModal';
import ResetPasswordSuccessModal from './Components/ResetPasswordSuccessModal';
import * as PATH from '../../../../CentralisedLink';
import countryOptions from '../../../../Constants/countryOptions';
import { fetchUserModuleDetailsAct } from '../../../../Redux/Dashboard/External/AdminUsers/Actions/FetchUserModuleDetailsAction';
import { updatePendingRequestAct } from '../../../../Redux/Dashboard/External/AdminUsers/Actions/UpdatePendingRequestAction';
import { approvePendingRequestAct } from '../../../../Redux/Dashboard/External/AdminUsers/Actions/ApprovePendingRequestAction';
import { rejectPendingRequestAct } from '../../../../Redux/Dashboard/External/AdminUsers/Actions/RejectPendingRequestAction';
import { fetchPendingRequestCountAct } from '../../../../Redux/Dashboard/External/AdminUsers/Actions/FetchPendingRequestCountAction';
import { updateUserAct } from '../../../../Redux/Dashboard/External/AdminUsers/Actions/UpdateUserAction';
import { deleteUserAct } from '../../../../Redux/Dashboard/External/AdminUsers/Actions/DeleteUserAction';
import { useFeatureFlags } from '../../../../FeatureFlags/FeatureFlagsContext';
import { FEATURE_FLAGS } from '../../../../FeatureFlags/featureFlagConstants';

const PAGE_SIZE = 7;
const FILTERABLE_FIELDS = [
  'typeOfUser',
  'role',
  'region',
  'company',
  'country',
  'manager',
  'isManager',
  'canDemo',
];

const DEFAULT_FILTERS = {
  typeOfUser: '',
  role: '',
  region: '',
  company: '',
  country: '',
  manager: '',
  isManager: '',
  canDemo: '',
  status: '',
};

const normalizeRoleValue = (value) => (value === 'DSOM SE' ? 'DSOM' : value || '');
// const PENDING_ROLE_OPTIONS = ['DSOM', 'DDE', 'R&D', 'Sales', 'Admin', 'Others'];
const PENDING_ROLE_OPTIONS = ['R&D','SQA','PM','Channel Partner','DSOM','GOSI','DCOE','FEDERAL','Services','CSM', 'DDE','Sales', 'Admin', 'Others'];
const DEFAULT_ROLE_FALLBACK = 'Not Mentioned';

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || '-';

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
};

const formatStatus = (value) => {
  if (!value) return 'Pending';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
};

const formatBoolean = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return value || '-';
};

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === 'Yes') return true;
  if (value === 'No') return false;
  return value;
};

const buildName = (user) => {
  if (user?.fullname) return user.fullname;

  const name = [user?.firstname, user?.lastname].filter(Boolean).join(' ');
  return name || '-';
};

const getRoleDisplayValue = (value) => normalizeRoleValue(value) || DEFAULT_ROLE_FALLBACK;

const createPendingRequestDraft = (user) => {
  const normalizedRole = normalizeRoleValue(user?.role);
  const isKnownRole = PENDING_ROLE_OPTIONS.includes(normalizedRole) && normalizedRole !== 'Others';

  return {
    ...user,
    role: isKnownRole ? normalizedRole : normalizedRole ? 'Others' : '',
    customRole: isKnownRole ? '' : normalizedRole,
  };
};

const resolvePendingDraftRole = (draft) =>
  draft?.role === 'Others'
    ? (draft?.customRole || '').trim()
    : normalizeRoleValue(draft?.role || draft?.roleOfUser);

const createAllUsersDraft = (user) => {
  const normalizedRole = normalizeRoleValue(user?.role);
  const isKnownRole = PENDING_ROLE_OPTIONS.includes(normalizedRole) && normalizedRole !== 'Others';

  return {
    ...user,
    role: isKnownRole ? normalizedRole : normalizedRole ? 'Others' : '',
    customRole: isKnownRole ? '' : normalizedRole,
  };
};

const resolveAllUsersDraftRole = (draft) =>
  draft?.role === 'Others'
    ? (draft?.customRole || '').trim()
    : normalizeRoleValue(draft?.role || draft?.roleOfUser);

const mapAdminUser = (user, index, dateField, idField) => ({
  ...user,
  rowKey: user?.[idField] || (idField === 'requestId' ? index : user?.id || index),
  requestId: user?.requestId || '',
  userId: user?.userId || '',
  name: buildName(user),
  firstName: user?.firstname || user?.firstName || '',
  lastName: user?.lastname || user?.lastName || '',
  email: user?.email || '',
  typeOfUser: user?.typeOfUser || '',
  role: normalizeRoleValue(user?.roleOfUser || user?.role),
  roleDisplay: getRoleDisplayValue(user?.roleOfUser || user?.role),
  countryRegion: [user?.country, user?.region].filter(Boolean).join(' / ') || '-',
  country: user?.country || '-',
  requestedAt: formatDate(user?.requestedAt),
  creationTime: formatDate(user?.creationTime),
  company: user?.company || '',
  status: formatStatus(user?.status),
  businessUnit: user?.bizUnit || user?.businessUnit || '',
  bizUnit: user?.bizUnit || user?.businessUnit || '',
  region: user?.region || '',
  manager: user?.manager || '',
  isManager: formatBoolean(user?.isManager),
  canDemo: formatBoolean(user?.canDemo),
  displayDate: formatDate(user?.[dateField]),
});

const getCountryFromDraft = (draft) => {
  if (draft?.country) return draft.country;
  if (!draft?.countryRegion) return '';

  return draft.countryRegion.split('/')[0].trim();
};

const buildPendingRequestPayload = (draft) => ({
  firstname: draft?.firstName || draft?.firstname || '',
  lastname: draft?.lastName || draft?.lastname || '',
  region: draft?.region || '',
  subregion: draft?.subregion || '',
  company: draft?.company || '',
  manager: draft?.manager || '',
  typeOfUser: draft?.typeOfUser || '',
  roleOfUser:
    draft?.role === 'Others'
      ? (draft?.customRole || '').trim()
      : normalizeRoleValue(draft?.role || draft?.roleOfUser),
  isManager: parseBoolean(draft?.isManager),
  canDemo: parseBoolean(draft?.canDemo),
  businessUnit: draft?.businessUnit || '',
  country: getCountryFromDraft(draft),
});

const buildUserPayload = (draft) => ({
  userId: draft?.userId || '',
  firstname: draft?.firstName || draft?.firstname || '',
  lastname: draft?.lastName || draft?.lastname || '',
  fullname: draft?.name || draft?.fullname || '',
  email: draft?.email || '',
  company: draft?.company || '',
  typeOfUser: draft?.typeOfUser || '',
  roleOfUser: resolveAllUsersDraftRole(draft),
  country: getCountryFromDraft(draft),
  region: draft?.region || '',
  businessUnit: draft?.bizUnit || draft?.businessUnit || '',
  manager: draft?.manager || '',
  isManager: parseBoolean(draft?.isManager),
  canDemo: parseBoolean(draft?.canDemo),
});

const getEmptyFilters = (tab) => (
  tab === 'pending' ? { ...DEFAULT_FILTERS } : { ...DEFAULT_FILTERS, status: '' }
);

const getFilterFields = (tab) => (
  tab === 'pending' ? [...FILTERABLE_FIELDS, 'status'] : FILTERABLE_FIELDS
);

const hasActiveFilters = (filters, tab) =>
  getFilterFields(tab).some((field) => filters?.[field]);

const matchesFilters = (user, filters, tab) =>
  getFilterFields(tab).every((field) => {
    if (!filters?.[field]) return true;
    return String(user?.[field] || '').toLowerCase() === String(filters[field]).toLowerCase();
  });

const getUniqueOptions = (data, key) =>
  [...new Set(
    (Array.isArray(data) ? data : [])
      .map((item) => item?.[key])
      .filter((value) => value && value !== '-')
  )].sort((a, b) => String(a).localeCompare(String(b)));

const UsersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const {
    data: userModuleDetails,
    loading: isUserModuleLoading,
    error: userModuleError,
  } = useSelector((state) => state.AdminUserModuleDetails || {});
  const isUpdatePendingRequestLoading = useSelector(
    (state) => state.AdminUpdatePendingRequest?.loading
  );
  const isApprovePendingRequestLoading = useSelector(
    (state) => state.AdminApprovePendingRequest?.loading
  );
  const isRejectPendingRequestLoading = useSelector(
    (state) => state.AdminRejectPendingRequest?.loading
  );
  const isUpdateUserLoading = useSelector((state) => state.AdminUpdateUser?.loading);
  const isDeleteUserLoading = useSelector((state) => state.AdminDeleteUser?.loading);

  const [pendingRequests, setPendingRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [selectedAllUser, setSelectedAllUser] = useState(null);
  const [isAllUsersViewModalOpen, setIsAllUsersViewModalOpen] = useState(false);
  const [isAllUsersEditMode, setIsAllUsersEditMode] = useState(false);
  const [allUsersEditDraft, setAllUsersEditDraft] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    pending: getEmptyFilters('pending'),
    all: getEmptyFilters('all'),
  });
  const [draftFilters, setDraftFilters] = useState(getEmptyFilters('pending'));
  const [isCreateAccountOpen, setIsCreateAccountOpen] = useState(false);
  const [createAccountCredentials, setCreateAccountCredentials] = useState(null);
  const [selectedResetPasswordUser, setSelectedResetPasswordUser] = useState(null);
  const [resetPasswordCredentials, setResetPasswordCredentials] = useState(null);

  useEffect(() => {
    dispatch(fetchUserModuleDetailsAct());
  }, [dispatch]);

  useEffect(() => {
    const mappedPendingRequests = Array.isArray(userModuleDetails?.pendingRequests)
      ? userModuleDetails.pendingRequests.map((user, index) => mapAdminUser(user, index, 'requestedAt', 'requestId'))
      : [];
    const mappedUsers = Array.isArray(userModuleDetails?.users)
      ? userModuleDetails.users.map((user, index) => mapAdminUser(user, index, 'creationTime', 'userId'))
      : [];

    setPendingRequests(mappedPendingRequests);
    setAllUsers(mappedUsers);
  }, [userModuleDetails]);

  const pendingCount = userModuleDetails?.counts?.pendingRequests ?? pendingRequests.length;
  const allUsersCount = userModuleDetails?.counts?.totalUsers ?? allUsers.length;

  const activeData = activeTab === 'pending' ? pendingRequests : allUsers;
  const activeFilters = appliedFilters[activeTab];
  const filterOptionsData = activeTab === 'pending' ? pendingRequests : allUsers;
  const hasSearchOrFilters = searchTerm.trim() || hasActiveFilters(activeFilters, activeTab);

  const filterOptions = useMemo(() => ({
    typeOfUser: getUniqueOptions(filterOptionsData, 'typeOfUser'),
    role: getUniqueOptions(filterOptionsData, 'role'),
    region: getUniqueOptions(filterOptionsData, 'region'),
    company: getUniqueOptions(filterOptionsData, 'company'),
    country: getUniqueOptions(filterOptionsData, 'country'),
    manager: getUniqueOptions(filterOptionsData, 'manager'),
    isManager: getUniqueOptions(filterOptionsData, 'isManager'),
    canDemo: getUniqueOptions(filterOptionsData, 'canDemo'),
    status: getUniqueOptions(filterOptionsData, 'status'),
  }), [filterOptionsData]);

  const filteredByTabData = useMemo(() => (
    activeData.filter((user) => matchesFilters(user, activeFilters, activeTab))
  ), [activeData, activeFilters, activeTab]);

  const filteredData = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return filteredByTabData;
    }

    return filteredByTabData.filter((user) => {
      return (
        user.name.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query)
      );
    });
  }, [filteredByTabData, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedData = filteredData.slice(
    (safeCurrentPage - 1) * PAGE_SIZE,
    safeCurrentPage * PAGE_SIZE
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const openFilterModal = () => {
    setDraftFilters({ ...appliedFilters[activeTab] });
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
    setDraftFilters({ ...appliedFilters[activeTab] });
  };

  const handleFilterChange = (field, value) => {
    setDraftFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      [activeTab]: { ...draftFilters },
    }));
    setCurrentPage(1);
    setIsFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    setDraftFilters(getEmptyFilters(activeTab));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const openViewModal = (user) => {
    setSelectedRequest(user);
    setIsViewModalOpen(true);
    setIsEditMode(false);
    setEditDraft(null);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setIsEditMode(false);
    setEditDraft(null);
  };

  const closeApproveModal = () => {
    setIsApproveModalOpen(false);
  };

  const handleApproveFromView = () => {
    setIsViewModalOpen(false);
    setIsApproveModalOpen(true);
  };

  const closeAllModals = () => {
    setIsViewModalOpen(false);
    setIsApproveModalOpen(false);
    setIsEditMode(false);
    setEditDraft(null);
  };

  const refreshPendingRequests = () => {
    dispatch(fetchUserModuleDetailsAct());
    dispatch(fetchPendingRequestCountAct());
  };

  const handleEditToggle = () => {
    if (!selectedRequest) return;

    setEditDraft(createPendingRequestDraft(selectedRequest));
    setIsEditMode(true);
  };

  const handleEditFieldChange = (field, value) => {
    setEditDraft((prev) => {
      const updatedDraft = {
        ...prev,
        [field]: value,
      };

      if (field === 'role' && value !== 'Others') {
        updatedDraft.customRole = '';
      }

      return updatedDraft;
    });
  };

  const handleCancelEdit = () => {
    setEditDraft(null);
    setIsEditMode(false);
  };

  const handleSaveEdit = async () => {
    if (!editDraft) return;
    if (!editDraft.requestId) {
      showAdminError('Update Failed', 'Request ID is required to update an account request.');
      return;
    }

    if (editDraft.role === 'Others' && !(editDraft.customRole || '').trim()) {
      showAdminError('Update Failed', 'Please enter a custom role.');
      return;
    }

    try {
      const data = await dispatch(
        updatePendingRequestAct(editDraft.requestId, buildPendingRequestPayload(editDraft))
      );

      if (!data) {
        showAdminError('Update Failed', 'Please try again.');
        return;
      }

      showAdminSuccess('Account request updated successfully.');
      const resolvedRole = resolvePendingDraftRole(editDraft);
      setSelectedRequest({
        ...editDraft,
        role: resolvedRole,
        roleDisplay: getRoleDisplayValue(resolvedRole),
      });
      closeViewModal();
      refreshPendingRequests();
    } catch (error) {
      showAdminError('Update Failed', error?.message || 'Please try again.');
    }
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest?.requestId) return;

    try {
      const data = await dispatch(approvePendingRequestAct(selectedRequest.requestId));

      if (!data) {
        showAdminError('Approve Failed', 'Please try again.');
        return;
      }

      showAdminSuccess('Account request approved successfully.');

      if (
        !isFeatureEnabled(FEATURE_FLAGS.EnableEmailNotifications)
        && data?.password
      ) {
        setCreateAccountCredentials({
          fullName: selectedRequest.name
            || [selectedRequest.firstName, selectedRequest.lastName].filter(Boolean).join(' '),
          email: selectedRequest.email,
          username: selectedRequest.email,
          password: data.password,
        });
      }

      closeAllModals();
      refreshPendingRequests();
    } catch (error) {
      showAdminError('Approve Failed', error?.message || 'Please try again.');
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest?.requestId) return;

    try {
      const data = await dispatch(rejectPendingRequestAct(selectedRequest.requestId));

      if (!data) {
        showAdminError('Reject Failed', 'Please try again.');
        return;
      }

      showAdminSuccess('Account request rejected successfully.');
      closeViewModal();
      refreshPendingRequests();
    } catch (error) {
      showAdminError('Reject Failed', error?.message || 'Please try again.');
    }
  };

  const renderField = (label, field, options = null, readOnly = false) => {
    const currentValue = isEditMode && editDraft ? editDraft[field] : selectedRequest[field];
    const selectOptions = options && currentValue && !options.includes(currentValue)
      ? [currentValue, ...options]
      : options;
    const isDisplayOnly = readOnly || field === 'email';
    const valueClassName = isEditMode && readOnly
      ? 'admin-users-modal__value admin-users-modal__value--field'
      : 'admin-users-modal__value';
    const displayValue = field === 'role' ? getRoleDisplayValue(currentValue) : currentValue;
    const shouldShowCustomRoleInput =
      field === 'role' && isEditMode && editDraft?.role === 'Others';
    const selectPlaceholder = field === 'country' ? 'Select Country' : 'Select';

    return (
      <div className={`admin-users-modal__field ${field === 'email' ? 'admin-users-modal__field--full' : ''}`}>
        <span className="admin-users-modal__label">{label}</span>
        {isEditMode && !isDisplayOnly ? (
          <div className="admin-users-modal__input-stack">
            {selectOptions ? (
              <select
                className="admin-users-modal__input"
                value={currentValue || ''}
                onChange={(event) => handleEditFieldChange(field, event.target.value)}
              >
                <option value="">{selectPlaceholder}</option>
                {selectOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="admin-users-modal__input"
                value={currentValue || ''}
                onChange={(event) => handleEditFieldChange(field, event.target.value)}
                readOnly={readOnly}
              />
            )}

            {shouldShowCustomRoleInput && (
              <input
                type="text"
                className="admin-users-modal__input"
                value={editDraft?.customRole || ''}
                onChange={(event) => handleEditFieldChange('customRole', event.target.value)}
                placeholder="Enter custom role"
              />
            )}
          </div>
        ) : (
          <span className={valueClassName}>{displayValue}</span>
        )}
      </div>
    );
  };

  const openAllUsersViewModal = (user) => {
    setSelectedAllUser(user);
    setIsAllUsersViewModalOpen(true);
    setIsAllUsersEditMode(false);
    setAllUsersEditDraft(null);
  };

  const closeAllUsersViewModal = () => {
    setIsAllUsersViewModalOpen(false);
    setIsAllUsersEditMode(false);
    setAllUsersEditDraft(null);
  };

  const handleAllUsersHeaderAction = async () => {
    if (!selectedAllUser) return;

    if (!isAllUsersEditMode) {
      setAllUsersEditDraft(createAllUsersDraft(selectedAllUser));
      setIsAllUsersEditMode(true);
      return;
    }

    if (!allUsersEditDraft) return;
    if (!allUsersEditDraft.userId) {
      showAdminError('Update Failed', 'User ID is required to update a user.');
      return;
    }

    if (allUsersEditDraft.role === 'Others' && !(allUsersEditDraft.customRole || '').trim()) {
      showAdminError('Update Failed', 'Please enter a custom role.');
      return;
    }

    if (!allUsersEditDraft.email) {
      showAdminError('Update Failed', 'Email is required to update a user.');
      return;
    }

    try {
      const data = await dispatch(updateUserAct(buildUserPayload(allUsersEditDraft)));

      if (!data) {
        showAdminError('Update Failed', 'Please try again.');
        return;
      }

      showAdminSuccess('User account details were updated successfully.');
      const resolvedRole = resolveAllUsersDraftRole(allUsersEditDraft);
      setSelectedAllUser({
        ...allUsersEditDraft,
        role: resolvedRole,
        roleDisplay: getRoleDisplayValue(resolvedRole),
      });
      setIsAllUsersEditMode(false);
      setAllUsersEditDraft(null);
      dispatch(fetchUserModuleDetailsAct());
    } catch (error) {
      showAdminError('Update Failed', error?.message || 'Please try again.');
    }
  };

  const handleCancelAllUsersEdit = () => {
    if (isAllUsersEditMode) {
      setIsAllUsersEditMode(false);
      setAllUsersEditDraft(null);
      return;
    }

    closeAllUsersViewModal();
  };

  const handleAllUsersFieldChange = (field, value) => {
    setAllUsersEditDraft((prev) => {
      const updatedDraft = {
        ...prev,
        [field]: value,
      };

      if (field === 'role' && value !== 'Others') {
        updatedDraft.customRole = '';
      }

      return updatedDraft;
    });
  };

  const renderAllUsersField = (label, field, options = null, readOnly = false) => {
    const currentValue =
      isAllUsersEditMode && allUsersEditDraft ? allUsersEditDraft[field] : selectedAllUser[field];
    const selectOptions = options && currentValue && !options.includes(currentValue)
      ? [currentValue, ...options]
      : options;
    const isDisplayOnly = readOnly || field === 'email';
    const valueClassName = isAllUsersEditMode && readOnly
      ? 'admin-users-modal__value admin-users-modal__value--field'
      : 'admin-users-modal__value';
    const selectPlaceholder = field === 'country' ? 'Select Country' : 'Select';
    const displayValue = field === 'role' ? getRoleDisplayValue(currentValue) : currentValue;
    const shouldShowCustomRoleInput =
      field === 'role' && isAllUsersEditMode && allUsersEditDraft?.role === 'Others';

    return (
      <div className={`admin-users-modal__field ${field === 'email' ? 'admin-users-modal__field--full' : ''}`}>
        <span className="admin-users-modal__label">{label}</span>
        {isAllUsersEditMode && !isDisplayOnly ? (
          <div className="admin-users-modal__input-stack">
            {selectOptions ? (
              <select
                className="admin-users-modal__input"
                value={currentValue || ''}
                onChange={(event) => handleAllUsersFieldChange(field, event.target.value)}
              >
                <option value="">{selectPlaceholder}</option>
                {selectOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="admin-users-modal__input"
                value={currentValue || ''}
                onChange={(event) => handleAllUsersFieldChange(field, event.target.value)}
                readOnly={readOnly}
              />
            )}

            {shouldShowCustomRoleInput && (
              <input
                type="text"
                className="admin-users-modal__input"
                value={allUsersEditDraft?.customRole || ''}
                onChange={(event) => handleAllUsersFieldChange('customRole', event.target.value)}
                placeholder="Enter custom role"
              />
            )}
          </div>
        ) : (
          <span className={valueClassName}>{displayValue}</span>
        )}
      </div>
    );
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const openCreateAccountModal = () => {
    setIsCreateAccountOpen(true);
  };

  const closeCreateAccountModal = () => {
    setIsCreateAccountOpen(false);
  };

  const handleCreateAccountSuccess = (credentials) => {
    setIsCreateAccountOpen(false);
    setCreateAccountCredentials(credentials);
    dispatch(fetchUserModuleDetailsAct());
  };

  const closeCreateAccountSuccessModal = () => {
    setCreateAccountCredentials(null);
  };

  const openResetPasswordModal = (user) => {
    setSelectedResetPasswordUser({
      name: user.name,
      email: user.email,
    });
  };

  const closeResetPasswordModal = () => {
    setSelectedResetPasswordUser(null);
  };

  const handleResetPasswordSuccess = (credentials) => {
    setSelectedResetPasswordUser(null);
    setResetPasswordCredentials(credentials);
  };

  const closeResetPasswordSuccessModal = () => {
    setResetPasswordCredentials(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    const email = (userToDelete.email || '').trim();

    if (!email) {
      showAdminError('Delete Failed', 'User email is required to delete a user.');
      return;
    }

    try {
      const data = await dispatch(deleteUserAct({ email }));

      if (!data) {
        showAdminError('Delete Failed', 'Please try again.');
        return;
      }

      showAdminSuccess('User account was deleted successfully.');

      if (selectedAllUser?.userId === userToDelete.userId) {
        closeAllUsersViewModal();
      }

      closeDeleteModal();
      dispatch(fetchUserModuleDetailsAct());
    } catch (error) {
      showAdminError('Delete Failed', error?.message || 'Please try again.');
    }
  };

  return (
    <>
      <Navbar activeTab="ADMIN" />
      <main className="admin-users-page">
        <div className="container">
          <section className="admin-users-page__intro">
            <div className="admin-users-page__intro-top">
              <div className="admin-users-page__title-row">
                <button
                  type="button"
                  className="admin-users-page__back-button"
                  onClick={() => navigate(PATH.ADMIN_PATH)}
                  aria-label="Back to Admin"
                >
                  <FaChevronLeft />
                </button>
                <div className="admin-users-page__title-icon">
                  <FaUsers />
                </div>
                <div>
                  <h1 className="admin-users-page__title">Account Management</h1>
                  <p className="admin-users-page__subtitle">
                    Manage account requests and existing user accounts
                  </p>
                </div>
              </div>

              <div className="admin-users-page__header-actions">
                <button
                  type="button"
                  className="admin-users-page__create-button"
                  onClick={openCreateAccountModal}
                >
                  <FaPlus />
                  <span>Create Account</span>
                </button>
              </div>
            </div>

            <div className="admin-users-summary">
              <div className="admin-users-summary__card">
                <div className="admin-users-summary__icon admin-users-summary__icon--pending">
                  <FaBell />
                </div>
                <div>
                  <div className="admin-users-summary__label">Pending Request</div>
                  <div
                    className={`admin-users-summary__value ${pendingCount === 0 ? 'is-zero' : ''}`}
                  >
                    {pendingCount}
                  </div>
                </div>
              </div>

              <div className="admin-users-summary__card">
                <div className="admin-users-summary__icon admin-users-summary__icon--users">
                  <FaUsers />
                </div>
                <div>
                  <div className="admin-users-summary__label">All Users</div>
                  <div
                    className={`admin-users-summary__value ${allUsersCount === 0 ? 'is-zero' : ''}`}
                  >
                    {allUsersCount}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="admin-users-panel">
            <div className="admin-users-tabs">
              <button
                type="button"
                className={`admin-users-tabs__button ${activeTab === 'pending' ? 'is-active' : ''}`}
                onClick={() => handleTabChange('pending')}
              >
                Pending Requests
              </button>
              <button
                type="button"
                className={`admin-users-tabs__button ${activeTab === 'all' ? 'is-active' : ''}`}
                onClick={() => handleTabChange('all')}
              >
                All Users
              </button>
            </div>

            <div className="admin-users-toolbar">
              <div className="admin-users-search">
                <FaSearch className="admin-users-search__icon" />
                <input
                  type="text"
                  className="admin-users-search__input"
                  placeholder="Search name or email"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>

              <button type="button" className="admin-users-filter-button" onClick={openFilterModal}>
                <FaFilter />
                <span>Filters</span>
              </button>
            </div>

            <div className="admin-users-table-wrap">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Type of User</th>
                    <th>Role</th>
                    <th>Country / Region</th>
                    <th>{activeTab === 'pending' ? 'Requested On' : 'Created On'}</th>
                    <th>Company</th>
                    {activeTab === 'pending' && <th>Status</th>}
                    <th className="admin-users-table__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isUserModuleLoading ? (
                    <tr>
                      <td
                        colSpan={activeTab === 'pending' ? 9 : 8}
                        className="admin-users-table__empty"
                      >
                        Loading users...
                      </td>
                    </tr>
                  ) : userModuleError ? (
                    <tr>
                      <td
                        colSpan={activeTab === 'pending' ? 9 : 8}
                        className="admin-users-table__empty"
                      >
                        {String(userModuleError)}
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((user) => (
                      <tr key={user.rowKey}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.typeOfUser}</td>
                        <td>{activeTab === 'pending' ? user.roleDisplay : user.role}</td>
                        <td>{user.countryRegion}</td>
                        <td>{activeTab === 'pending' ? user.requestedAt : user.creationTime}</td>
                        <td>{user.company}</td>
                        {activeTab === 'pending' && (
                          <td>
                            <span className="admin-users-status-badge">{user.status || 'Pending'}</span>
                          </td>
                        )}
                        <td className="admin-users-table__actions-col">
                          <div className="admin-users-actions">
                            <button
                              type="button"
                              className="admin-users-action admin-users-action--view"
                              onClick={() =>
                                activeTab === 'pending' ? openViewModal(user) : openAllUsersViewModal(user)
                              }
                            >
                              <FaEye />
                              <span>View</span>
                            </button>
                            {activeTab !== 'pending' && (
                              <>
                                <button
                                  type="button"
                                  className="admin-users-action admin-users-action--reset"
                                  onClick={() => openResetPasswordModal(user)}
                                >
                                  <FaKey />
                                  <span>Reset Password</span>
                                </button>
                                <button
                                  type="button"
                                  className="admin-users-action admin-users-action--delete"
                                  onClick={() => openDeleteModal(user)}
                                >
                                  <FaTrash />
                                  <span>Delete</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={activeTab === 'pending' ? 9 : 8}
                        className="admin-users-table__empty"
                      >
                        {hasSearchOrFilters
                          ? activeTab === 'pending'
                            ? 'No matching pending requests found.'
                            : 'No matching users found.'
                          : activeTab === 'pending'
                            ? 'No pending requests found.'
                            : 'No users found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-users-pagination">
              <div className="admin-users-pagination__nav">
                <button
                  type="button"
                  className="admin-users-pagination__button"
                  onClick={() => setCurrentPage(1)}
                  disabled={safeCurrentPage === 1}
                  aria-label="First page"
                  title="First page"
                >
                  <FaAngleDoubleLeft />
                </button>
                <button
                  type="button"
                  className="admin-users-pagination__button"
                  onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                  disabled={safeCurrentPage === 1}
                  aria-label="Previous page"
                  title="Previous page"
                >
                  <FaChevronLeft />
                </button>
              </div>

              <div className="admin-users-pagination__status">
                Page {safeCurrentPage} of {totalPages}
              </div>

              <div className="admin-users-pagination__nav">
                <button
                  type="button"
                  className="admin-users-pagination__button"
                  onClick={() =>
                    setCurrentPage((page) => Math.min(page + 1, totalPages))
                  }
                  disabled={safeCurrentPage === totalPages}
                  aria-label="Next page"
                  title="Next page"
                >
                  <FaChevronRight />
                </button>
                <button
                  type="button"
                  className="admin-users-pagination__button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={safeCurrentPage === totalPages}
                  aria-label="Last page"
                  title="Last page"
                >
                  <FaAngleDoubleRight />
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      {isViewModalOpen && selectedRequest && (
        <div className="admin-users-modal-overlay" onClick={closeViewModal}>
          <div
            className={`admin-users-modal admin-users-modal--view ${isEditMode ? 'is-edit-mode' : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-users-modal__header">
              <h2 className="admin-users-modal__title">View User Request</h2>
              <div className="admin-users-modal__header-actions">
                {!isEditMode && (
                  <button
                    type="button"
                    className="admin-users-modal__text-action"
                    onClick={handleEditToggle}
                  >
                    Edit
                  </button>
                )}
                <button
                  type="button"
                  className="admin-users-modal__icon-button"
                  onClick={closeViewModal}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="admin-users-modal__body">
              <div className="admin-users-modal__section">
                <h3 className="admin-users-modal__section-title">Personal Info</h3>
                <div className="admin-users-modal__grid">
                  {renderField('First Name', 'firstName')}
                  {renderField('Last Name', 'lastName')}
                  {renderField('Email', 'email')}
                </div>
              </div>

              <div className="admin-users-modal__section">
                <h3 className="admin-users-modal__section-title">Organizational Info</h3>
                <div className="admin-users-modal__grid">
                  {renderField('Company', 'company')}
                  {renderField('Type of User', 'typeOfUser', ['Internal SE', 'External SE', 'Technical User','Sales'])}
                  {renderField('Business Unit', 'businessUnit', ['DSOM'])}
                  {renderField('Role of User', 'role', PENDING_ROLE_OPTIONS)}
                </div>
              </div>

              <div className="admin-users-modal__section">
                <h3 className="admin-users-modal__section-title">Additional Info</h3>
                <div className="admin-users-modal__grid">
                  {renderField('Country', 'country', countryOptions)}
                  {renderField('Region', 'region', ['APJ', 'APJJ', 'EMEA', 'NA', 'LATAM'])}
                  {renderField('Manager', 'manager')}
                  {renderField('Is Manager?', 'isManager', ['Yes', 'No'])}
                  {renderField('Can Demo?', 'canDemo', ['Yes', 'No'])}
                  {renderField('Requested On', 'requestedAt', null, true)}
                </div>
              </div>
            </div>

            <div className="admin-users-modal__footer">
              {isEditMode ? (
                <>
                  <button
                    type="button"
                    className="admin-users-modal__button admin-users-modal__button--save"
                    onClick={handleSaveEdit}
                    disabled={isUpdatePendingRequestLoading}
                  >
                    {isUpdatePendingRequestLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="admin-users-modal__button admin-users-modal__button--cancel"
                    onClick={handleCancelEdit}
                    disabled={isUpdatePendingRequestLoading}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <div className="admin-users-modal__footer-group">
                    <button
                      type="button"
                      className="admin-users-modal__button admin-users-modal__button--approve"
                      onClick={handleApproveFromView}
                      disabled={isRejectPendingRequestLoading}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="admin-users-modal__button admin-users-modal__button--reject"
                      onClick={handleRejectRequest}
                      disabled={isRejectPendingRequestLoading}
                    >
                      {isRejectPendingRequestLoading ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                  <div className="admin-users-modal__footer-group admin-users-modal__footer-group--right">
                    <button
                      type="button"
                      className="admin-users-modal__button admin-users-modal__button--cancel"
                      onClick={closeViewModal}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isFilterModalOpen && (
        <div className="admin-users-modal-overlay" onClick={closeFilterModal}>
          <div
            className="admin-users-modal admin-users-modal--filters"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-users-modal__header">
              <h2 className="admin-users-modal__title">
                {activeTab === 'pending' ? 'Pending Request Filters' : 'All Users Filters'}
              </h2>
              <button
                type="button"
                className="admin-users-modal__icon-button"
                onClick={closeFilterModal}
              >
                <FaTimes />
              </button>
            </div>

            <div className="admin-users-modal__body">
              <div className="admin-users-filter-grid">
                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Type of User</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.typeOfUser}
                    onChange={(event) => handleFilterChange('typeOfUser', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.typeOfUser.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Role</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.role}
                    onChange={(event) => handleFilterChange('role', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.role.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Region</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.region}
                    onChange={(event) => handleFilterChange('region', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.region.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Company</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.company}
                    onChange={(event) => handleFilterChange('company', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.company.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Country</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.country}
                    onChange={(event) => handleFilterChange('country', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.country.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Manager</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.manager}
                    onChange={(event) => handleFilterChange('manager', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.manager.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Is Manager</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.isManager}
                    onChange={(event) => handleFilterChange('isManager', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.isManager.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-users-filter-field">
                  <span className="admin-users-modal__label">Can Demo</span>
                  <select
                    className="admin-users-modal__input"
                    value={draftFilters.canDemo}
                    onChange={(event) => handleFilterChange('canDemo', event.target.value)}
                  >
                    <option value="">All</option>
                    {filterOptions.canDemo.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {activeTab === 'pending' && (
                  <div className="admin-users-filter-field">
                    <span className="admin-users-modal__label">Status</span>
                    <select
                      className="admin-users-modal__input"
                      value={draftFilters.status}
                      onChange={(event) => handleFilterChange('status', event.target.value)}
                    >
                      <option value="">All</option>
                      {filterOptions.status.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-users-modal__footer">
              <div className="admin-users-modal__footer-group">
                <button
                  type="button"
                  className="admin-users-modal__button admin-users-modal__button--save"
                  onClick={handleApplyFilters}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="admin-users-modal__button admin-users-modal__button--cancel"
                  onClick={handleResetFilters}
                >
                  Reset
                </button>
              </div>
              <div className="admin-users-modal__footer-group admin-users-modal__footer-group--right">
                <button
                  type="button"
                  className="admin-users-modal__button admin-users-modal__button--cancel"
                  onClick={closeFilterModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isApproveModalOpen && selectedRequest && (
        <div className="admin-users-modal-overlay" onClick={closeApproveModal}>
          <div
            className="admin-users-modal admin-users-modal--confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-users-confirm__icon">
              <FaExclamationTriangle />
            </div>
            <h2 className="admin-users-modal__title admin-users-modal__title--center">
              Approve User Request?
            </h2>
            <p className="admin-users-confirm__text">
              Approving this request will create a new user account and send credentials to{' '}
              <strong>{selectedRequest.email}</strong>.
            </p>

            <div className="admin-users-confirm__actions">
              <button
                type="button"
                className="admin-users-modal__button admin-users-modal__button--approve"
                onClick={handleApproveRequest}
                disabled={isApprovePendingRequestLoading}
              >
                {isApprovePendingRequestLoading ? 'Approving...' : 'Approve'}
              </button>
              <button
                type="button"
                className="admin-users-modal__button admin-users-modal__button--cancel"
                onClick={closeApproveModal}
                disabled={isApprovePendingRequestLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isAllUsersViewModalOpen && selectedAllUser && (
        <div className="admin-users-modal-overlay" onClick={closeAllUsersViewModal}>
          <div
            className={`admin-users-modal admin-users-modal--view ${isAllUsersEditMode ? 'is-edit-mode' : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-users-modal__header">
              <h2 className="admin-users-modal__title">View User</h2>
              <div className="admin-users-modal__header-actions">
                <button
                  type="button"
                  className="admin-users-modal__text-action"
                  onClick={handleAllUsersHeaderAction}
                  disabled={isUpdateUserLoading}
                >
                  {isUpdateUserLoading ? 'Saving...' : isAllUsersEditMode ? 'Save' : 'Edit'}
                </button>
                <button
                  type="button"
                  className="admin-users-modal__icon-button"
                  onClick={closeAllUsersViewModal}
                  disabled={isUpdateUserLoading}
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <div className="admin-users-modal__body">
              <div className="admin-users-modal__section">
                <h3 className="admin-users-modal__section-title">Personal Info</h3>
                <div className="admin-users-modal__grid">
                  {renderAllUsersField('First Name', 'firstName')}
                  {renderAllUsersField('Last Name', 'lastName')}
                  {renderAllUsersField('Email', 'email')}
                </div>
              </div>

              <div className="admin-users-modal__section">
                <h3 className="admin-users-modal__section-title">Organizational Info</h3>
                <div className="admin-users-modal__grid">
                  {renderAllUsersField('Company', 'company')}
                  {renderAllUsersField('Type of User', 'typeOfUser', ['Internal SE', 'External SE', 'Technical User', 'Admin','Sales'])}
                  {renderAllUsersField('Business Unit', 'businessUnit', ['DSOM'])}
                  {renderAllUsersField('Role of User', 'role', ['R&D','SQA','PM','Channel Partner','DSOM','GOSI','DCOE','FEDERAL','Services','CSM', 'DDE','Sales', 'Admin', 'Others'])}
                </div>
              </div>

              <div className="admin-users-modal__section">
                <h3 className="admin-users-modal__section-title">Additional Info</h3>
                <div className="admin-users-modal__grid">
                  {renderAllUsersField('Country', 'country', countryOptions)}
                  {renderAllUsersField('Region', 'region', ['APJ', 'APJJ', 'EMEA', 'NA', 'LATAM'])}
                  {renderAllUsersField('Manager', 'manager')}
                  {renderAllUsersField('Is Manager?', 'isManager', ['Yes', 'No'])}
                  {renderAllUsersField('Can Demo?', 'canDemo', ['Yes', 'No'])}
                  {renderAllUsersField('Created On', 'creationTime', null, true)}
                </div>
              </div>
            </div>

            <div className="admin-users-modal__footer">
              <div className="admin-users-modal__footer-group admin-users-modal__footer-group--right">
                <button
                  type="button"
                  className="admin-users-modal__button admin-users-modal__button--cancel"
                  onClick={handleCancelAllUsersEdit}
                  disabled={isUpdateUserLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && userToDelete && (
        <div className="admin-users-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="admin-users-modal admin-users-modal--confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-users-confirm__icon admin-users-confirm__icon--delete">
              <FaExclamationTriangle />
            </div>
            <h2 className="admin-users-modal__title admin-users-modal__title--center">
              Delete User?
            </h2>
            <p className="admin-users-confirm__text">
              You are about to delete this user account for <strong>{userToDelete.email}</strong>.
              This is a sensitive action and should only be done if you are sure.
            </p>

            <div className="admin-users-confirm__actions">
              <button
                type="button"
                className="admin-users-modal__button admin-users-modal__button--delete"
                onClick={handleConfirmDelete}
                disabled={isDeleteUserLoading}
              >
                {isDeleteUserLoading ? 'Deleting...' : 'Delete'}
              </button>
              <button
                type="button"
                className="admin-users-modal__button admin-users-modal__button--cancel"
                onClick={closeDeleteModal}
                disabled={isDeleteUserLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateAccountModal
        isOpen={isCreateAccountOpen}
        onClose={closeCreateAccountModal}
        onSuccess={handleCreateAccountSuccess}
      />

      <CreateAccountSuccessModal
        credentials={createAccountCredentials}
        onDone={closeCreateAccountSuccessModal}
      />

      <ResetPasswordModal
        isOpen={Boolean(selectedResetPasswordUser)}
        user={selectedResetPasswordUser}
        onClose={closeResetPasswordModal}
        onSuccess={handleResetPasswordSuccess}
      />

      <ResetPasswordSuccessModal
        credentials={resetPasswordCredentials}
        onDone={closeResetPasswordSuccessModal}
      />
    </>
  );
};

export default UsersPage;
